// Deterministic provenance matching: find where an extracted line item's amount is
// printed on the invoice PDF, using nothing but the geometry of the PDF's own text layer.
//
// Two findings from a spike over 2,278 production line items across 52 invoices shape this
// file, and both are counter-intuitive enough to be worth stating up front:
//
//   1. Descriptions are useless as an anchor. Exact description matching located 0% of
//      rows, because the extraction model rewrites what the PDF prints - the PDF says
//      "COOKIE FORTUNE VANILLA", the model emits "COOKIE FORTUNE VANILLA 350 1 EA". Do not
//      add description matching here; it was measured, not guessed at.
//   2. Amounts alone are ambiguous, but amounts scoped to one column are not. Matching an
//      amount against any money token on the page was 40.5% unique / 59.4% ambiguous,
//      because a quantity-1 line prints the same number in UNIT PRICE and in EXTENSION.
//      Scoping the search to the extension column took it to 96.8% unique / 0.8% ambiguous.
//
// Everything here is pure and free of DOM or PDF.js types except extractWords, so the same
// logic can be ported to the server later without being rewritten.

// Amounts on these invoices always print cents. Requiring exactly two decimal places is
// what keeps item numbers, quantities and pack sizes out of the money columns.
const MONEY_SHAPE = /^\d{1,3}(?:,\d{3})*\.\d{2}$/

// Right-aligned columns line up to well under a point; 4 leaves room for the fact that
// PDF.js derives token widths from font metrics rather than measuring the glyphs.
const COLUMN_TOLERANCE = 4

// Some PDF text items report a height of 0. Give those a plausible line height so the
// highlight box is still visible.
const FALLBACK_WORD_HEIGHT = 10

// A column has to carry roughly one token per line item before it can be the amount
// column. See selectExtensionColumn for why this matters so much.
const MIN_DENSE_TOKENS = 3
const DENSITY_RATIO = 0.5

// Parses a token into signed cents, or null if it is not an amount.
export function parseMoneyCents(text) {
  const raw = String(text ?? "").trim()
  if (raw === "") return null

  // A leading minus, a trailing minus and wrapping parentheses all mean "credit" depending
  // on the vendor's template. Strip the sign markers first, then apply the strict shape
  // test to what is left.
  const negative = raw.startsWith("-") || raw.endsWith("-") || (raw.startsWith("(") && raw.endsWith(")"))
  const core = raw.replace(/^[-($]+/, "").replace(/[-)]+$/, "")
  if (!MONEY_SHAPE.test(core)) return null

  const cents = Math.round(Number.parseFloat(core.replace(/,/g, "")) * 100)
  return negative ? -cents : cents
}

// Turns a PDF.js text content payload into a flat word list in top-left origin
// coordinates. Pass a scale-1 viewport: the viewport transform is linear in scale, so
// storing unit coordinates lets a zoom change re-project existing matches instead of
// re-parsing the page.
export function extractWords(textContent, viewport) {
  const words = []

  textContent.items.forEach((item) => {
    const text = typeof item.str === "string" ? item.str.trim() : ""
    if (text === "") return

    // convertToViewportPoint also performs the PDF bottom-left to screen top-left flip.
    const [originX, baselineY] = viewport.convertToViewportPoint(item.transform[4], item.transform[5])
    const [endX, ascenderY] = viewport.convertToViewportPoint(
      item.transform[4] + item.width,
      item.transform[5] + item.height,
    )

    const x0 = Math.min(originX, endX)
    const x1 = Math.max(originX, endX)
    let y0 = Math.min(baselineY, ascenderY)
    const y1 = Math.max(baselineY, ascenderY)
    if (y1 - y0 < 1) y0 = y1 - FALLBACK_WORD_HEIGHT

    // PDF.js emits one item per text-showing operator. That is usually a single column
    // cell, but can be a whole run such as "3 EA 80.66". Split on whitespace and apportion
    // the run's width by character offset; the last token's right edge - the edge column
    // discovery clusters on - comes out exact either way.
    const unitWidth = (x1 - x0) / text.length

    for (const match of text.matchAll(/\S+/g)) {
      words.push({
        text: match[0],
        x0: x0 + (match.index * unitWidth),
        x1: x0 + ((match.index + match[0].length) * unitWidth),
        y0,
        y1,
      })
    }
  })

  return words
}

// Clusters the money tokens into right-aligned columns, ordered left to right.
export function discoverMoneyColumns(words, { tolerance = COLUMN_TOLERANCE } = {}) {
  const moneyWords = words
    .map((word) => ({ ...word, cents: parseMoneyCents(word.text) }))
    .filter((word) => word.cents !== null)
    .sort((left, right) => left.x1 - right.x1)

  const columns = []

  moneyWords.forEach((word) => {
    const current = columns[columns.length - 1]

    // Money columns are right-aligned, so the right edge is the stable feature. Compare
    // against the column's first right edge rather than its last so a long tail of
    // near-misses cannot chain two adjacent columns into one.
    if (current && word.x1 - current.rightX <= tolerance) {
      current.tokens.push(word)
      return
    }

    columns.push({ rightX: word.x1, tokens: [word] })
  })

  return columns
}

// Picks the extension (line amount) column out of the discovered columns.
export function selectExtensionColumn(columns) {
  if (columns.length === 0) return null

  const busiest = columns.reduce((max, column) => Math.max(max, column.tokens.length), 0)
  const threshold = Math.max(MIN_DENSE_TOKENS, Math.ceil(busiest * DENSITY_RATIO))

  // Rightmost *dense* column - deliberately not rightmost outright. An earlier version of
  // this heuristic took the rightmost money column and scored 5% on the corpus, because
  // the rightmost money column on these invoices is the "PAY THIS AMOUNT" box, which holds
  // exactly one number. Requiring a column to carry a real share of the document's money
  // tokens before it can win took the same corpus to 96.8%. Please do not simplify this
  // back to "last column"; it looks like a no-op and is a 20x regression.
  const dense = columns.filter((column) => column.tokens.length >= threshold)
  if (dense.length === 0) return null

  return dense[dense.length - 1]
}

// Indexes a column's tokens by value so per-row lookup is O(1). Built once per document.
export function buildAmountIndex(tokens) {
  const bySigned = new Map()
  const byAbsolute = new Map()

  const append = (map, key, token) => {
    const existing = map.get(key)
    if (existing) existing.push(token)
    else map.set(key, [token])
  }

  tokens.forEach((token) => {
    append(bySigned, token.cents, token)
    append(byAbsolute, Math.abs(token.cents), token)
  })

  return { bySigned, byAbsolute }
}

// --- header fields ------------------------------------------------------------------------
//
// Line amounts are found by value plus column. A header field has no column, so the second
// piece of evidence is its printed label instead: find the tokens that read exactly the
// extracted value, then keep the one sitting where a label's value belongs - immediately to
// its right, or on the line directly beneath it.
//
// Anchoring on the value first is what makes this safe rather than clever. Every location
// this can return is a token that reads exactly the invoice number we are claiming it is,
// so the worst available failure is pointing at a second, equally correct printing of the
// same number - not at a different number. A label-first search would not have that
// property, and header fields are the ones a reviewer would trust most.
//
// Measured over the 54 hand-labelled invoices in data/eval: invoice number located uniquely
// 54/54, 0 ambiguous. See the note on selectExtensionColumn about how narrow that corpus is.

// Two words belong to the same printed line within this many points.
const LINE_TOLERANCE = 3

// How far a value may sit from its label before the two stop being related.
const MAX_GAP_RIGHT = 220
const MAX_GAP_BELOW = 40

// Second best must be at least this much worse before the best is called unique.
const AMBIGUITY_MARGIN = 12

// Invoice numbers are compared with punctuation and case removed, so "#376600", "376600"
// and "376-600" are the same string. Nothing else about the token is trusted.
export function normalizeIdentifier(value) {
  return String(value ?? "").replace(/[^0-9a-z]/gi, "").toUpperCase()
}

// Label spellings seen across the corpus. The bare "invoice" entry is load-bearing:
// Performance Foodservice prints a column heading - "DATE INVOICE SLM ACCT NO" with the
// values on the line beneath - and without it every invoice from that vendor, which is
// ~94% of production, finds no label at all.
export const INVOICE_NUMBER_LABELS = [
  [/^invoice\s*#?$/i, /^(no\.?|number|num|#)$/i],
  [/^invoice\s*(#|no\.?|number)$/i],
  [/^inv\s*(#|no\.?)$/i],
  [/^document$/i, /^(no\.?|number)$/i],
  [/^invoice$/i],
]

function verticalCentre(word) {
  return (word.y0 + word.y1) / 2
}

// Groups words into printed lines, each ordered left to right.
export function groupLines(words) {
  const sorted = [...words].sort(
    (left, right) =>
      (left.pageNumber || 0) - (right.pageNumber || 0) ||
      verticalCentre(left) - verticalCentre(right) ||
      left.x0 - right.x0,
  )

  const lines = []
  sorted.forEach((word) => {
    const current = lines[lines.length - 1]
    if (
      current &&
      current.pageNumber === word.pageNumber &&
      Math.abs(verticalCentre(word) - current.centre) <= LINE_TOLERANCE
    ) {
      current.words.push(word)
      return
    }

    lines.push({ pageNumber: word.pageNumber, centre: verticalCentre(word), words: [word] })
  })

  return lines
}

function boundingBox(words) {
  return {
    x0: Math.min(...words.map((word) => word.x0)),
    x1: Math.max(...words.map((word) => word.x1)),
    y0: Math.min(...words.map((word) => word.y0)),
    y1: Math.max(...words.map((word) => word.y1)),
    pageNumber: words[0].pageNumber,
  }
}

// Finds label phrases. A label is one token ("INVOICE#") or a run of adjacent tokens on one
// line ("Invoice", "Number"); each pattern is a list of tests, one per token.
export function findLabelAnchors(lines, patterns) {
  const anchors = []

  lines.forEach((line) => {
    patterns.forEach((pattern) => {
      for (let start = 0; start + pattern.length <= line.words.length; start += 1) {
        const slice = line.words.slice(start, start + pattern.length)
        if (slice.every((word, index) => pattern[index].test(word.text))) {
          anchors.push(boundingBox(slice))
        }
      }
    })
  })

  return anchors
}

// How far a reader travels from a label to a candidate value. Infinity when the candidate is
// not somewhere a value could be: only to the right on the same line, or below with
// horizontal overlap, count at all.
function readingDistance(anchor, candidate) {
  if (anchor.pageNumber !== candidate.pageNumber) return Infinity

  if (Math.abs(verticalCentre(anchor) - verticalCentre(candidate)) <= LINE_TOLERANCE) {
    const gap = candidate.x0 - anchor.x1
    if (gap < -2 || gap > MAX_GAP_RIGHT) return Infinity
    return Math.max(gap, 0)
  }

  const drop = candidate.y0 - anchor.y1
  if (drop < 0 || drop > MAX_GAP_BELOW) return Infinity

  // Beneath a column heading the value sits under it, not off to one side.
  const overlap = Math.min(anchor.x1, candidate.x1) - Math.max(anchor.x0, candidate.x0)
  if (overlap < -12) return Infinity

  return drop + Math.max(0, -overlap)
}

// Locates a header value by its printed label. Same contract as locateAmount: "located"
// only on a unique winner.
export function locateLabelledValue(words, { value, patterns }) {
  const wanted = normalizeIdentifier(value)
  if (wanted === "") return { state: "missing", candidates: [] }

  const candidates = words.filter((word) => normalizeIdentifier(word.text) === wanted)
  if (candidates.length === 0) return { state: "missing", candidates: [] }

  const anchors = findLabelAnchors(groupLines(words), patterns)
  if (anchors.length === 0) return { state: "missing", candidates }

  const scored = candidates
    .map((candidate) => ({
      candidate,
      distance: Math.min(...anchors.map((anchor) => readingDistance(anchor, candidate))),
    }))
    .filter((entry) => Number.isFinite(entry.distance))
    .sort((left, right) => left.distance - right.distance)

  if (scored.length === 0) return { state: "missing", candidates }
  if (scored.length === 1) return { state: "located", token: scored[0].candidate, candidates }

  const tied = scored.filter((entry) => entry.distance - scored[0].distance < AMBIGUITY_MARGIN)
  if (tied.length === 1) return { state: "located", token: scored[0].candidate, candidates }

  // Multi-page invoices reprint the header on every page, so the same value at the same
  // place on different pages is one location repeated, not a genuine ambiguity. Take the
  // earliest page. Two tied candidates on one page is a real ambiguity.
  const pages = new Set(tied.map((entry) => entry.candidate.pageNumber))
  if (pages.size !== tied.length) return { state: "ambiguous", candidates: tied.map((e) => e.candidate) }

  const earliest = tied.reduce((best, entry) =>
    entry.candidate.pageNumber < best.candidate.pageNumber ? entry : best,
  )
  return { state: "located", token: earliest.candidate, candidates }
}

// Locates one amount. Returns "located" only on a unique hit - a wrong highlight is worse
// than no highlight, so ambiguity and absence are both reported rather than guessed at.
export function locateAmount(index, cents) {
  if (!index || !Number.isFinite(cents)) return { state: "missing", candidates: [] }

  // Try the signed value first. Credits print as "50.00-" on some templates and as a plain
  // "50.00" on others, so fall back to an absolute-value match - but only when the signed
  // lookup found nothing, so a charge is never matched to a credit while its own cell is
  // sitting in the same column.
  const signed = index.bySigned.get(cents) || []
  const candidates = signed.length > 0 ? signed : (index.byAbsolute.get(Math.abs(cents)) || [])

  if (candidates.length === 1) return { state: "located", token: candidates[0], candidates }
  if (candidates.length === 0) return { state: "missing", candidates: [] }

  return { state: "ambiguous", candidates }
}
